import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import api, { resourcesAPI } from '../lib/api';
import { 
    MapPin, 
    Building2, 
    ExternalLink, 
    Lock, 
    LogIn,
    List,
    Map as MapIcon,
    Users
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
    return L.divIcon({
        className: 'custom-marker',
        html: `<div style="
            background-color: ${color};
            width: 24px;
            height: 24px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -24],
    });
};

const resourceIcon = createCustomIcon('#14B8A6'); // Teal for resources
const chapterIcon = createCustomIcon('#8B5CF6');  // Purple for chapters
const featuredIcon = createCustomIcon('#F59E0B'); // Amber for featured

// US State coordinates for geocoding resources
const STATE_COORDINATES = {
    "Alabama": { lat: 32.806671, lng: -86.79113 },
    "Alaska": { lat: 61.370716, lng: -152.404419 },
    "Arizona": { lat: 33.729759, lng: -111.431221 },
    "Arkansas": { lat: 34.969704, lng: -92.373123 },
    "California": { lat: 36.116203, lng: -119.681564 },
    "Colorado": { lat: 39.059811, lng: -105.311104 },
    "Connecticut": { lat: 41.597782, lng: -72.755371 },
    "Delaware": { lat: 39.318523, lng: -75.507141 },
    "Florida": { lat: 27.766279, lng: -81.686783 },
    "Georgia": { lat: 33.040619, lng: -83.643074 },
    "Hawaii": { lat: 21.094318, lng: -157.498337 },
    "Idaho": { lat: 44.240459, lng: -114.478828 },
    "Illinois": { lat: 40.349457, lng: -88.986137 },
    "Indiana": { lat: 39.849426, lng: -86.258278 },
    "Iowa": { lat: 42.011539, lng: -93.210526 },
    "Kansas": { lat: 38.5266, lng: -96.726486 },
    "Kentucky": { lat: 37.66814, lng: -84.670067 },
    "Louisiana": { lat: 31.169546, lng: -91.867805 },
    "Maine": { lat: 44.693947, lng: -69.381927 },
    "Maryland": { lat: 39.063946, lng: -76.802101 },
    "Massachusetts": { lat: 42.230171, lng: -71.530106 },
    "Michigan": { lat: 43.326618, lng: -84.536095 },
    "Minnesota": { lat: 45.694454, lng: -93.900192 },
    "Mississippi": { lat: 32.741646, lng: -89.678696 },
    "Missouri": { lat: 38.456085, lng: -92.288368 },
    "Montana": { lat: 46.921925, lng: -110.454353 },
    "Nebraska": { lat: 41.12537, lng: -98.268082 },
    "Nevada": { lat: 38.313515, lng: -117.055374 },
    "New Hampshire": { lat: 43.452492, lng: -71.563896 },
    "New Jersey": { lat: 40.298904, lng: -74.521011 },
    "New Mexico": { lat: 34.840515, lng: -106.248482 },
    "New York": { lat: 42.165726, lng: -74.948051 },
    "North Carolina": { lat: 35.630066, lng: -79.806419 },
    "North Dakota": { lat: 47.528912, lng: -99.784012 },
    "Ohio": { lat: 40.388783, lng: -82.764915 },
    "Oklahoma": { lat: 35.565342, lng: -96.928917 },
    "Oregon": { lat: 44.572021, lng: -122.070938 },
    "Pennsylvania": { lat: 40.590752, lng: -77.209755 },
    "Rhode Island": { lat: 41.680893, lng: -71.51178 },
    "South Carolina": { lat: 33.856892, lng: -80.945007 },
    "South Dakota": { lat: 44.299782, lng: -99.438828 },
    "Tennessee": { lat: 35.747845, lng: -86.692345 },
    "Texas": { lat: 31.054487, lng: -97.563461 },
    "Utah": { lat: 40.150032, lng: -111.862434 },
    "Vermont": { lat: 44.045876, lng: -72.710686 },
    "Virginia": { lat: 37.769337, lng: -78.169968 },
    "Washington": { lat: 47.400902, lng: -121.490494 },
    "West Virginia": { lat: 38.491226, lng: -80.954453 },
    "Wisconsin": { lat: 44.268543, lng: -89.616508 },
    "Wyoming": { lat: 42.755966, lng: -107.30249 },
    "National": { lat: 39.8283, lng: -98.5795 },
};

// City coordinates for more accurate placement
const CITY_COORDINATES = {
    "Chicago": { lat: 41.8781, lng: -87.6298 },
    "Miami": { lat: 25.7617, lng: -80.1918 },
    "Austin": { lat: 30.2672, lng: -97.7431 },
    "Los Angeles": { lat: 34.0522, lng: -118.2437 },
    "New York": { lat: 40.7128, lng: -74.0060 },
    "Atlanta": { lat: 33.7490, lng: -84.3880 },
    "Orlando": { lat: 28.5383, lng: -81.3792 },
    "Columbus": { lat: 39.9612, lng: -82.9988 },
    "Phoenix": { lat: 33.4484, lng: -112.0740 },
    "Raleigh": { lat: 35.7796, lng: -78.6382 },
    "Denver": { lat: 39.7392, lng: -104.9903 },
    "Seattle": { lat: 47.6062, lng: -122.3321 },
    "San Francisco": { lat: 37.7749, lng: -122.4194 },
    "Boston": { lat: 42.3601, lng: -71.0589 },
    "Dallas": { lat: 32.7767, lng: -96.7970 },
    "Houston": { lat: 29.7604, lng: -95.3698 },
    "Birmingham": { lat: 33.5186, lng: -86.8104 },
    "Montgomery": { lat: 32.3792, lng: -86.3077 },
    "Irvine": { lat: 33.6846, lng: -117.8265 },
    "San Diego": { lat: 32.7157, lng: -117.1611 },
    "San Jose": { lat: 37.3382, lng: -121.8863 },
    "Santa Barbara": { lat: 34.4208, lng: -119.6982 },
    "Riverside": { lat: 33.9533, lng: -117.3962 },
    "La Jolla": { lat: 32.8328, lng: -117.2713 },
    "Concord": { lat: 37.9780, lng: -122.0311 },
    "Woodland Hills": { lat: 34.1684, lng: -118.6059 },
    "Richmond": { lat: 37.5407, lng: -77.4360 },
    "Fort Lauderdale": { lat: 26.1224, lng: -80.1373 },
    "Palm Beach": { lat: 26.7056, lng: -80.0364 },
    "Gainesville": { lat: 29.6516, lng: -82.3248 },
    "Weston": { lat: 26.1003, lng: -80.3998 },
    "Winter Garden": { lat: 28.5653, lng: -81.5862 },
    "Jupiter": { lat: 26.9342, lng: -80.0942 },
    "Athens": { lat: 33.9519, lng: -83.3576 },
    "Roswell": { lat: 34.0232, lng: -84.3616 },
    "Marietta": { lat: 33.9526, lng: -84.5499 },
    "Peachtree Corners": { lat: 33.9701, lng: -84.2215 },
    "Bloomington": { lat: 39.1653, lng: -86.5264 },
    "Fishers": { lat: 39.9568, lng: -86.0131 },
    "Zionsville": { lat: 39.9509, lng: -86.2628 },
    "Lexington": { lat: 38.0406, lng: -84.5037 },
    "College Park": { lat: 38.9807, lng: -76.9369 },
    "Columbia": { lat: 39.2037, lng: -76.8610 },
    "Hunt Valley": { lat: 39.4979, lng: -76.6413 },
    "Rockville": { lat: 39.0840, lng: -77.1528 },
    "Owings": { lat: 38.7137, lng: -76.6016 },
    "Baltimore": { lat: 39.2904, lng: -76.6122 },
    "Minneapolis": { lat: 44.9778, lng: -93.2650 },
    "Saint Paul": { lat: 44.9537, lng: -93.0900 },
    "Plymouth": { lat: 45.0105, lng: -93.4558 },
    "San Antonio": { lat: 29.4241, lng: -98.4936 },
    "Waco": { lat: 31.5493, lng: -97.1467 },
    "El Paso": { lat: 31.7619, lng: -106.4850 },
    "New Braunfels": { lat: 29.7030, lng: -98.1245 },
    "Naperville": { lat: 41.7508, lng: -88.1535 },
    "Warrenville": { lat: 41.8214, lng: -88.1834 },
    "Madison": { lat: 43.0731, lng: -89.4012 },
    "Menasha": { lat: 44.2022, lng: -88.4465 },
    "Salt Lake City": { lat: 40.7608, lng: -111.8910 },
    "Scarsdale": { lat: 40.9887, lng: -73.7846 },
    "Fairfax": { lat: 38.8462, lng: -77.3064 },
    "Virginia Beach": { lat: 36.8529, lng: -75.9780 },
};

// Helper to get coordinates for a resource
const getResourceCoordinates = (resource) => {
    // Try city first
    if (resource.city && CITY_COORDINATES[resource.city]) {
        const coords = CITY_COORDINATES[resource.city];
        // Add small random offset to prevent markers from stacking
        return {
            lat: coords.lat + (Math.random() - 0.5) * 0.05,
            lng: coords.lng + (Math.random() - 0.5) * 0.05,
        };
    }
    // Fall back to state
    if (resource.state && STATE_COORDINATES[resource.state]) {
        const coords = STATE_COORDINATES[resource.state];
        // Add larger offset for state-level placement
        return {
            lat: coords.lat + (Math.random() - 0.5) * 0.5,
            lng: coords.lng + (Math.random() - 0.5) * 0.5,
        };
    }
    return null;
};

// Component to fit map bounds
const FitBounds = ({ resources, chapters }) => {
    const map = useMap();
    
    useEffect(() => {
        const allCoords = [];
        
        // Add chapter coordinates
        chapters.forEach(ch => {
            allCoords.push([ch.lat, ch.lng]);
        });
        
        // Add resource coordinates
        resources.forEach(r => {
            const coords = getResourceCoordinates(r);
            if (coords) {
                allCoords.push([coords.lat, coords.lng]);
            }
        });
        
        if (allCoords.length > 0) {
            const bounds = L.latLngBounds(allCoords);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [map, resources, chapters]);
    
    return null;
};

const MapPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [resources, setResources] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showChapters, setShowChapters] = useState(true);
    const [showResources, setShowResources] = useState(true);

    useEffect(() => {
        if (!isAuthenticated || authLoading) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const [resourcesRes, chaptersRes] = await Promise.all([
                    resourcesAPI.getAll({ limit: 500 }),
                    api.get('/chapters')
                ]);
                setResources(resourcesRes.data);
                // Map chapters to include coordinates from STATE_COORDINATES
                const mapped = chaptersRes.data
                    .filter(ch => STATE_COORDINATES[ch.state])
                    .map(ch => ({
                        ...ch,
                        lat: STATE_COORDINATES[ch.state].lat,
                        lng: STATE_COORDINATES[ch.state].lng,
                    }));
                setChapters(mapped);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAuthenticated, authLoading]);

    // Show login required screen if not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-background" data-testid="map-page-login-required">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <Card className="text-center">
                        <CardContent className="pt-12 pb-10">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-primary" />
                            </div>
                            <h1 className="font-heading text-3xl font-bold mb-4">
                                Members Only
                            </h1>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                The Interactive Map is exclusively available to HAAPE members. 
                                Please log in with your member account to explore resources and chapters.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={login} size="lg" className="gap-2" data-testid="login-btn">
                                    <LogIn className="h-4 w-4" />
                                    Member Login
                                </Button>
                                <Link to="/apply">
                                    <Button variant="outline" size="lg" data-testid="apply-btn">
                                        Apply For Membership
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Get resources with valid coordinates
    const resourcesWithCoords = resources
        .map(r => ({
            ...r,
            coords: getResourceCoordinates(r),
        }))
        .filter(r => r.coords !== null);

    return (
        <div className="min-h-screen bg-background" data-testid="map-page">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
                                <MapIcon className="h-8 w-8 text-primary" />
                                Interactive Map
                            </h1>
                            <p className="text-muted-foreground">
                                Explore resources and chapters across the United States
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/directory">
                                <Button variant="outline" className="gap-2">
                                    <List className="h-4 w-4" />
                                    List View
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {/* Legend / Filters */}
                    <div className="flex flex-wrap items-center gap-4 mt-4">
                        <button
                            onClick={() => setShowChapters(!showChapters)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                                showChapters 
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                                    : 'bg-muted text-muted-foreground'
                            }`}
                        >
                            <div className="w-3 h-3 rounded-full bg-purple-500" />
                            Chapters ({chapters.length})
                        </button>
                        <button
                            onClick={() => setShowResources(!showResources)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                                showResources 
                                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' 
                                    : 'bg-muted text-muted-foreground'
                            }`}
                        >
                            <div className="w-3 h-3 rounded-full bg-teal-500" />
                            Resources ({resourcesWithCoords.length})
                        </button>
                        <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            Featured
                        </div>
                    </div>
                </div>
            </div>

            {/* Map Container */}
            <div className="relative" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                        <div className="text-center">
                            <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
                            <p className="text-muted-foreground">Loading map data...</p>
                        </div>
                    </div>
                ) : (
                    <MapContainer
                        center={[39.8283, -98.5795]}
                        zoom={4}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        
                        <FitBounds resources={resourcesWithCoords} chapters={showChapters ? chapters : []} />

                        {/* Chapter Markers */}
                        {showChapters && chapters.map((chapter) => (
                            <Marker
                                key={chapter.name}
                                position={[chapter.lat, chapter.lng]}
                                icon={chapterIcon}
                            >
                                <Popup>
                                    <div className="min-w-[200px]">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Users className="h-4 w-4 text-purple-600" />
                                            <span className="font-semibold">{chapter.name}</span>
                                        </div>
                                        <Badge variant="secondary" className="mb-2">
                                            HAAPE Chapter
                                        </Badge>
                                        <p className="text-sm text-muted-foreground">
                                            {chapter.description}
                                        </p>
                                        <Link 
                                            to={`/directory?state=${encodeURIComponent(chapter.state)}`}
                                            className="text-sm text-primary hover:underline mt-2 inline-block"
                                        >
                                            View {chapter.state} resources →
                                        </Link>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}

                        {/* Resource Markers */}
                        {showResources && resourcesWithCoords.map((resource) => (
                            <Marker
                                key={resource.resource_id}
                                position={[resource.coords.lat, resource.coords.lng]}
                                icon={resource.is_featured ? featuredIcon : resourceIcon}
                            >
                                <Popup>
                                    <div className="min-w-[250px] max-w-[300px]">
                                        <div className="flex items-start gap-2 mb-2">
                                            <Building2 className="h-4 w-4 text-teal-600 mt-1 shrink-0" />
                                            <span className="font-semibold leading-tight">{resource.name}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                                            <MapPin className="h-3 w-3" />
                                            {resource.city ? `${resource.city}, ` : ''}{resource.state}
                                        </div>
                                        {resource.is_featured && (
                                            <Badge className="mb-2 bg-amber-500">Featured</Badge>
                                        )}
                                        <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                                            {resource.description}
                                        </p>
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {resource.organization_types?.slice(0, 2).map((type) => (
                                                <Badge key={type} variant="outline" className="text-xs">
                                                    {type}
                                                </Badge>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Link to={`/resource/${resource.resource_id}`}>
                                                <Button size="sm" variant="default">
                                                    View Details
                                                </Button>
                                            </Link>
                                            {resource.website && (
                                                <a 
                                                    href={resource.website} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                >
                                                    <Button size="sm" variant="outline" className="gap-1">
                                                        <ExternalLink className="h-3 w-3" />
                                                        Website
                                                    </Button>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}
            </div>
        </div>
    );
};

export default MapPage;
